import React, { useCallback, useEffect, useState } from 'react';
import { FiChevronLeft, FiChevronRight, FiHelpCircle } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';

const PROGRESS_DEBOUNCE_MS = 800;

/**
 * Player de aula com slides em PDF (imagens pré-rasterizadas pelo backend).
 * Irmão dos players YouTube/Vimeo: consome o mesmo contrato de callbacks de
 * course-classroom.jsx e reusa o CourseClassroomModalQuiz sem mudança.
 * Dados em dash-case (learning_system_contents).
 */
export function CourseClassroomPlayerSlides(props) {
  const { selectedItem, disclaimerModal, showQuizModal, setShowQuizModal, setCurrentQuiz, unansweredQuestions, onUpdateProgress, setUpdateContentNow } = props;
  const { t } = useTranslation();

  const slideDeck = selectedItem?.['slide-deck'];
  const pages = slideDeck?.pages || [];
  const totalSlides = slideDeck?.['page-count'] || pages.length;

  const [currentSlide, setCurrentSlide] = useState(1);
  const [maxSlideSeen, setMaxSlideSeen] = useState(1);

  // Troca de aula → volta ao primeiro slide
  useEffect(() => {
    setCurrentSlide(1);
    setMaxSlideSeen(1);
  }, [selectedItem?.id]);

  // Progresso: percent = round(maior slide visto / total * 100), com debounce
  // por navegação (não por intervalo). Último slide → 100 → envio imediato
  // (sem debounce), pra não perder o progresso final se o componente
  // desmontar antes do timer disparar; setUpdateContentNow só dispara aqui
  // APÓS o PATCH resolver.
  useEffect(() => {
    if (!totalSlides) return undefined;

    const percent = Math.round((maxSlideSeen / totalSlides) * 100);

    if (percent >= 100) {
      Promise.resolve(onUpdateProgress(100)).then(() => setUpdateContentNow(true));
      return undefined;
    }

    const timer = setTimeout(async () => {
      await onUpdateProgress(percent);
    }, PROGRESS_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [maxSlideSeen, totalSlides]);

  // Pré-carrega as imagens adjacentes
  useEffect(() => {
    [currentSlide - 1, currentSlide + 1].forEach(position => {
      const page = pages.find(p => p.position === position);
      if (page?.['image-url']) {
        const image = new Image();
        image.src = page['image-url'];
      }
    });
  }, [currentSlide, pages]);

  const pendingQuizAt = useCallback(position => (unansweredQuestions || []).find(quiz => quiz['slide-position'] === position), [unansweredQuestions]);

  const goNext = useCallback(() => {
    if (currentSlide >= totalSlides) return;

    // Quiz bloqueante: não avança de N para N+1 com quiz pendente na fronteira N
    const pendingQuiz = pendingQuizAt(currentSlide);
    if (pendingQuiz) {
      setCurrentQuiz(pendingQuiz);
      setShowQuizModal(true);
      return;
    }

    const next = currentSlide + 1;
    setCurrentSlide(next);
    setMaxSlideSeen(previous => Math.max(previous, next));
  }, [currentSlide, totalSlides, pendingQuizAt, setCurrentQuiz, setShowQuizModal]);

  const goPrev = useCallback(() => {
    setCurrentSlide(previous => Math.max(1, previous - 1));
  }, []);

  // Navegação por setas do teclado (suspensa com quiz/disclaimer abertos)
  useEffect(() => {
    const handleKeyDown = event => {
      const target = event.target;
      const tagName = target?.tagName;
      if (tagName === 'INPUT' || tagName === 'TEXTAREA' || tagName === 'SELECT' || target?.isContentEditable) return;
      if (showQuizModal || disclaimerModal) return;
      if (event.key === 'ArrowRight') goNext();
      if (event.key === 'ArrowLeft') goPrev();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [goNext, goPrev, showQuizModal, disclaimerModal]);

  if (!totalSlides) return null;

  const currentPage = pages.find(page => page.position === currentSlide);
  const counterText = t('lessons.slideCounter', { current: currentSlide, total: totalSlides });

  return (
    <div className="w-full">
      <img
        src={currentPage?.['image-url']}
        alt={`${counterText}: ${(selectedItem?.body || '').slice(0, 120)}`}
        className="w-full rounded-lg bg-neutral-100"
      />

      {/* Barra de progresso segmentada; marcador nas fronteiras com quiz pendente */}
      <div
        className="mt-3 flex items-center gap-1"
        data-testid="slides-progress-bar"
      >
        {Array.from({ length: totalSlides }, (_, index) => index + 1).map(position => (
          <div
            key={position}
            className="relative h-1 flex-1"
          >
            <div className={`h-1 w-full rounded-full ${position <= maxSlideSeen ? 'bg-primary' : 'bg-neutral-200'}`} />
            {position < totalSlides && pendingQuizAt(position) && <FiHelpCircle className="absolute -right-2 -top-1.5 z-10 h-4 w-4 rounded-full bg-white text-warning" />}
          </div>
        ))}
      </div>

      <div className="mt-3 flex items-center justify-between">
        <button
          type="button"
          className="btn btn--outline"
          aria-label={t('lessons.prevSlide')}
          disabled={currentSlide === 1}
          onClick={goPrev}
        >
          <FiChevronLeft />
        </button>

        <span
          className="text-sm text-neutral-700"
          data-testid="slide-counter"
        >
          {counterText}
        </span>

        <button
          type="button"
          className="btn btn--outline"
          aria-label={t('lessons.nextSlide')}
          disabled={currentSlide === totalSlides}
          onClick={goNext}
        >
          <FiChevronRight />
        </button>
      </div>
    </div>
  );
}
